import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { FeatureFlag } from "./types";
import { FeatureFlagRow } from "./FeatureFlagRow";
import { domainLabel, groupFlagsByDomain, isUngroupedDomain } from "./group-flags-by-domain";

interface FeatureFlagsListProps {
  readonly items: readonly FeatureFlag[];
}

export function FeatureFlagsList({ items }: FeatureFlagsListProps) {
  const { t } = useTranslation();
  const groups = useMemo(() => groupFlagsByDomain(items), [items]);

  return (
    <div className="space-y-8">
      {groups.map((group) => {
        const ungrouped = isUngroupedDomain(group.domain);
        const testidDomain = ungrouped ? "other" : group.domain;
        const heading = ungrouped
          ? t("featureFlags.group.other")
          : t(`featureFlags.group.domains.${group.domain}`, {
              defaultValue: domainLabel(group.domain),
            });
        const headingId = `feature-flag-group-${testidDomain}-heading`;

        return (
          <section
            key={group.domain || "__ungrouped__"}
            data-testid={`feature-flag-group-${testidDomain}`}
            aria-labelledby={headingId}
            className="space-y-3"
          >
            <h2 id={headingId} className="brand-eyebrow text-sm">
              {heading}
            </h2>
            <div className="space-y-3">
              {group.flags.map((flag) => (
                <FeatureFlagRow key={flag.id} flag={flag} />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
